import React, { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { CreditCard, LockKey, SealCheck, Clock, WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const money = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function StatusCard({ icon: Icon, tone, title, sub, testid }) {
  return (
    <div className="text-center py-6" data-testid={testid}>
      <Icon size={52} weight="duotone" className={`mx-auto ${tone}`} />
      <p className="font-heading font-bold text-2xl text-white mt-4">{title}</p>
      {sub && <p className="text-sm text-white/55 mt-2">{sub}</p>}
    </div>
  );
}

function MethodButton({ icon: Icon, title, sub, onClick, busy, testid, primary }) {
  return (
    <button onClick={onClick} disabled={busy} data-testid={testid}
      className={`w-full text-left rounded-2xl border p-4 flex items-center gap-4 transition-colors disabled:opacity-50 ${primary ? "border-emerald-400/50 bg-emerald-400/[0.07] hover:bg-emerald-400/[0.12]" : "border-white/10 bg-white/[0.02] hover:bg-white/[0.06]"}`}>
      <Icon size={28} weight="duotone" className={primary ? "text-emerald-300" : "text-sky-300"} />
      <span className="flex-1"><span className="block font-heading font-bold text-white">{title}</span><span className="block text-xs text-white/50">{sub}</span></span>
    </button>
  );
}

export default function PayLink() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get(`/pay/${token}`).then(({ data }) => setInfo(data)).catch((e) => setErr(apiError(e.response?.data?.detail))), [token]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!params.get("done")) return undefined;
    const t = setInterval(load, 3000);
    const stop = setTimeout(() => clearInterval(t), 30000);
    return () => { clearInterval(t); clearTimeout(stop); };
  }, [params, load]);

  const go = async (method) => {
    setBusy(true);
    try { const { data } = await api.post(`/pay/${token}/checkout`, { method }); window.location.href = data.url; }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); setBusy(false); }
  };

  const body = () => {
    if (err) return <StatusCard icon={WarningCircle} tone="text-red-400" title="Link not found" sub={err} testid="pay-invalid" />;
    if (!info) return <p className="text-white/40 text-center py-10">Loading…</p>;
    if (info.status === "paid") return <StatusCard icon={SealCheck} tone="text-emerald-400" title="Paid. Thank you!" sub={`${money(info.amount)} was received.`} testid="pay-paid" />;
    if (info.status === "processing") return <StatusCard icon={Clock} tone="text-sky-300" title="Payment in progress" sub="Your bank payment was submitted. ACH payments usually settle in 1 to 4 business days." testid="pay-processing" />;
    if (info.status === "expired") return <StatusCard icon={Clock} tone="text-white/40" title="This link has expired" sub="Please ask Asterism Services for a new payment link." testid="pay-expired" />;
    if (info.status !== "open") return <StatusCard icon={WarningCircle} tone="text-white/40" title={`This link is ${info.status}`} sub="Please contact Asterism Services." testid="pay-closed" />;
    if (params.get("done")) return <StatusCard icon={Clock} tone="text-sky-300" title="Confirming your payment…" testid="pay-confirming" />;
    if (!info.enabled) return <StatusCard icon={Clock} tone="text-white/40" title="Online payments are being activated" testid="pay-disabled" />;
    return (
      <div className="space-y-3" data-testid="pay-form">
        {info.last_error && <p className="rounded-xl border border-amber-400/30 bg-amber-400/[0.06] px-4 py-3 text-sm text-amber-100" data-testid="pay-last-error">The last attempt didn't go through: {info.last_error}. Please try again.</p>}
        <MethodButton icon={CreditCard} primary title={`Pay ${money(info.amount)} by card`} sub="Visa, Mastercard, Amex · USD · No extra fee" onClick={() => go("card")} busy={busy} testid="pay-card" />
        <p className="pt-2 text-[11px] text-white/40 flex items-center justify-center gap-1.5"><LockKey size={13} />Secured by Razorpay. Asterism never sees your card number.</p>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#05070a] text-white flex items-center justify-center px-4 py-12 relative overflow-hidden" data-testid="pay-page">
      <div className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full bg-sky-500/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -right-40 w-[520px] h-[520px] rounded-full bg-emerald-500/10 blur-3xl pointer-events-none" />
      <div className="relative w-full max-w-md">
        <div className="flex items-center gap-2.5 mb-8"><img src="/freightx-logo.webp" alt="Asterism Services" className="w-9 h-9 object-contain" /><span className="font-heading font-extrabold text-lg">Asterism Services</span></div>
        <div className="rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-xl p-7 sm:p-8">
          {info && info.status === "open" && (
            <div className="mb-7">
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-sky-300">Secure Payment</p>
              <p className="font-heading font-extrabold text-5xl mt-3 tabular-nums" data-testid="pay-amount">{money(info.amount)}</p>
              <p className="text-white/60 mt-2" data-testid="pay-description">{info.description}</p>
              {info.expires_at && <p className="text-xs text-white/35 mt-1">Link expires {new Date(info.expires_at).toLocaleDateString()}</p>}
            </div>
          )}
          {body()}
        </div>
      </div>
    </div>
  );
}
